/**
 * Changes to the events here should be represented on the Help Center page:
 * TODO: This page needs to be created :)
 * https://support.mobi2go.com/hc/en-us/articles/
 */

/**-----------------------------------------------------------------------------
 * Load in production only, or if forced
 */
import _ from 'lodash';

const load_analytics = /(\?|&)load_analytics/.test(document.location.search);

if (Mobi2Go.app.base_url === 'https://www.mobi2go.com' || load_analytics) {
    Mobi2Go.onReady(load);
}

function load() {
    const { Order } = Mobi2Go;
    const { Analytics } = Mobi2Go;
    const E = Analytics.EVENTS;

    /**-------------------------------------------------------------------------
     * Script Setup
     */
    // To support several we can pass as string with commas and then separate it here
    const web_property_id =
        Mobi2Go.headoffice.analytics_google_four_web_property_id;

    if (!web_property_id) {
        return;
    }

    let m2g_ga4;

    (function() {
        // Load the gtag.js script and link it to the UA4 ID
        const firstScriptElement = document.getElementsByTagName('script')[0];
        const scriptElement = document.createElement('script');
        scriptElement.async = true;
        scriptElement.src = `https://www.googletagmanager.com/gtag/js?id=${web_property_id}`;
        firstScriptElement.parentNode.insertBefore(
            scriptElement,
            firstScriptElement
        );
    })();

    /**-------------------------------------------------------------------------
     * Abstraction
     */

    function startSession() {
        // session starts automatically and it lasts for 30 mins
        window.dataLayer = window.dataLayer || [];

        m2g_ga4 = function() {
            window.dataLayer.push(arguments);
        };

        m2g_ga4('js', new Date());
        // Can support multiple properties but they would need to
        // be config
        m2g_ga4('config', web_property_id);

        m2g_ga4('set', {
            app_name: Mobi2Go.app.getPlatform(),
            app_id: `${
                Mobi2Go.headoffice.domain_name
            }-${Mobi2Go.app.getPlatform()}`,
            currency: Mobi2Go.headoffice.currency_abrv,
        });

        // Apps always use a host + pathname similar to
        // https://www.mobi2go.com/store/page/3418/mobile.
        // We need a mechanism to allow us to override
        // this in the case that analytics are captured based on
        // the client's actual storefront embed path.
        if (Mobi2Go.headoffice.storefront_embed_path) {
            // Note that the 'page' value will get used for all subsequent analytics pings
            m2g_ga4('set', {
                page: Mobi2Go.headoffice.storefront_embed_path,
            });
        }
    }

    function loadLastOrder(lastOrderState) {
        m2g_ga4('event', 'load_last_order', {
            send_to: web_property_id,
            order_id: lastOrderState.id,
        });
    }

    function identifyLocation(location) {
        if (location) {
            m2g_ga4('event', 'set_location', {
                send_to: web_property_id,
                location_name: location.name,
            });
        }
    }

    function identifyUser(customer) {
        if (customer) {
            m2g_ga4('set', { user_id: customer.id });
        }
    }

    function impressCategory(category) {
        _impressProducts(category.getProducts().toArray(), category.name);
        m2g_ga4('event', 'view_category', {
            send_to: web_property_id,
            category_name: category.name
        });
    }

    function impressMenu(menu) {
        _impressProducts(menu.getCategoryProducts().toArray(), menu.name);
        m2g_ga4('event', 'view_menu', {
            send_to: web_property_id,
            menu_name: menu.name
        });
    }

    function editProduct(product) {
        m2g_ga4('event', 'edit_product', {
            send_to: web_property_id,
            value: product.menu_product.price,
            items: [_trackProduct(product)],
        });
    }

    function addProductToOrder(product) {
        m2g_ga4('event', 'add_to_cart', {
            send_to: web_property_id,
            value: product.menu_product.price,
            items: [_trackProduct(product)],
        });
    }

    function removeProductFromOrder(product) {
        if (Order.isResetting()) {
            return;
        }
        m2g_ga4('event', 'remove_from_cart', {
            send_to: web_property_id,
            value: product.menu_product.price,
            items: [_trackProduct(product)],
        });
    }

    function startedCheckout(order) {
        m2g_ga4('event', 'begin_checkout', {
            send_to: web_property_id,
            value: order
                .getTotal()
                .getTaxExclusivePrice()
                .toFloat(),
            items: _trackAllProducts(),
        });
    }

    function purchase(order) {
        m2g_ga4('event', 'purchase', {
            send_to: web_property_id,
            transaction_id: order.getId(),
            value: order
                .getTotal()
                .getTaxExclusivePrice()
                .toFloat(),
            tax: order
                .getTotal()
                .getTaxPrice()
                .toFloat(),
            shipping: order.getDeliveryFee().toFloat(),
            coupon: order.getVoucherCode(),
            affiliation: order.getLocation().name,
            items: _trackAllProducts(),
        });
        m2g_ga4('event', 'order_confirmed');
    }

    function startPayment(order) {
        m2g_ga4('event', 'start_payment', {
            send_to: web_property_id,
            order_id: order.getId()
        });
    }

    function saveCreditCard() {
        m2g_ga4('event', 'save_credit_card', {send_to: web_property_id});
    }

    function completeRegistration() {
        m2g_ga4('event', 'sign_up', {send_to: web_property_id,});
    }

    function confirmedOrder(order) {
        if (order.getPaymentMethod() === 'offline') {
            checkoutOfflinePayment(order);
        }

        purchase(order);
    }

    function checkoutOfflinePayment(order) {
        m2g_ga4('event', 'payment_offline', {
            send_to: web_property_id,
            order_id: order.getId()
        });
    }

    function splitBill(order) {
        m2g_ga4('event', 'split_bill', {
            send_to: web_property_id,
            order_id: order.getId()
        });
    }

    function _impressProducts(menu_products, list_name) {
        _.each(menu_products, function(menu_product, position) {
            _impressProduct(menu_product, list_name, position + 1);
        });
    }

    function _impressProduct(menu_product, list_name, position) {
        m2g_ga4('event', 'view_item', {
            send_to: web_property_id,
            value: menu_product.price,
            items: [
                {
                    item_id: menu_product.id,
                    item_name: menu_product.name,
                    item_list_name: list_name,
                    item_category: menu_product.category
                        ? menu_product.category.name
                        : null,
                    index: position,
                },
            ],
        });
    }

    function _trackAllProducts() {
        return _.map(Order.getProducts(), _trackProduct);
    }

    function _trackProduct(product) {
        return {
            item_id: product.menu_product.id,
            item_name: product.menu_product.name,
            price: product.price.toFloat(),
            quantity: product.quantity,
            item_variant: product.size
                ? product.size.menu_modifier.name
                : null,
            item_category: product.menu_product.category
                ? product.menu_product.category.name
                : null,
            index: product.menu_product.category_list_position
                ? product.menu_product.category_list_position
                : null,
        };
    }

    /**-------------------------------------------------------------------------
     * Application Glue
     */

    // Need to configure first before sending the events
    startSession();

    Analytics.bind({
        LAST_ORDER_LOADED: loadLastOrder,
        IDENTIFY_LOCATION: identifyLocation,
        IDENTIFY_CUSTOMER: identifyUser,
        VIEWED_PRODUCT_MENU: impressMenu,
        VIEWED_PRODUCT_CATEGORY: impressCategory,
        ADDED_PRODUCT: addProductToOrder,
        EDITED_PRODUCT: editProduct,
        REMOVED_PRODUCT: removeProductFromOrder,
        STARTED_PAYMENT: startPayment,
        CREDIT_CARD_SAVED: saveCreditCard,
        CONFIRMED_ORDER: confirmedOrder,
        STARTED_CHECKOUT: startedCheckout,
        REGISTERED_CUSTOMER: completeRegistration,
        SPLIT_BILL: splitBill,
    });
}
